import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import {
  QueryClient,
  defaultShouldDehydrateQuery,
} from '@tanstack/react-query';
import type { PersistQueryClientProviderProps } from '@tanstack/react-query-persist-client';

interface QueryMetadata extends Record<string, unknown> {
  persist?: boolean;
}

/**
 * Module augmentation to add global type definitions for query and mutation meta descriptions.
 * This allows us to have type safety when using the `meta` field in useQuery/useMutation options.
 */
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: QueryMetadata;
    mutationMeta: QueryMetadata;
  }
}

declare module '@tanstack/query-core' {
  interface Register {
    queryMeta: QueryMetadata;
    mutationMeta: QueryMetadata;
  }
}

/**
 * React Query client instance with configured default options.
 *
 * Default Options:
 * - gcTime: 24 hours (86400000ms) - Time before inactive queries are garbage collected from cache
 * - staleTime: 12 hours (43200000ms) - Time before data is considered stale
 *
 * This client is used throughout the application for data fetching, caching, and synchronization.
 */
const client = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours - should match or exceed maxAge
      staleTime: 1000 * 60 * 60 * 12, // Data stays "fresh" for 12 hours
    },
  },
});

/**
 * Creates a persister that stores query cache data in sessionStorage.
 * This allows query results to persist across page refreshes within the same browser session.
 * Data is cleared when the browser tab is closed.
 */
const queryPersister = createAsyncStoragePersister({
  storage: globalThis.sessionStorage,
});

/**
 * Persistence configuration for React Query client.
 *
 * This configuration enables automatic persistence of query cache to sessionStorage,
 * allowing data to survive page refreshes within the same browser session.
 *
 * @property persister - The storage persister instance using sessionStorage
 * @property dehydrateOptions - Configuration for which queries should be persisted
 * @property dehydrateOptions.shouldDehydrateQuery - Function that determines if a query should be persisted.
 *   Returns true only for successful queries, except those explicitly marked with `meta: { persist: false }`.
 *   Errored and in-flight queries are never persisted: an error loses its class and message in JSON,
 *   and a pending promise cannot be restored.
 */
const persitenceOptions: PersistQueryClientProviderProps['persistOptions'] = {
  persister: queryPersister,
  dehydrateOptions: {
    shouldDehydrateQuery: query =>
      defaultShouldDehydrateQuery(query) && query.meta?.persist !== false,
  },
};

export { client, persitenceOptions };
